/**
 * PrinterProfilesPanel – pick / save named printer profiles (paper size +
 * orientation + custom mm). Lives above the paper-size selector in the Export
 * card so offices with several printers apply the right geometry in one click.
 */

import * as React from "react";
import { Printer, Plus, Trash2, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  listPrinterProfiles,
  savePrinterProfile,
  deletePrinterProfile,
  type PrinterProfile,
} from "@/utils/printerProfilesStore";
import { toastSuccess, toastInfo } from "@/utils/toast";
import { cn } from "@/lib/utils";
import {
  formatPaper,
  PAPER_LABELS,
  type CurrentPaper,
} from "@/utils/paperFormat";

// Re-export for backward compatibility (wizard imports formatPaper from here).
export { formatPaper, PAPER_LABELS, type CurrentPaper };

interface Props {
  /** Current paper setup (null = follow template). */
  current: CurrentPaper | null;
  /** Applied when the user picks a profile. */
  onApply: (profile: PrinterProfile) => void;
}

export function PrinterProfilesPanel({ current, onApply }: Props) {
  const [profiles, setProfiles] = React.useState<PrinterProfile[]>(() => listPrinterProfiles());
  const [open, setOpen] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState("");
  const [note, setNote] = React.useState("");

  function refresh() {
    setProfiles(listPrinterProfiles());
  }

  function handleSaveCurrent() {
    const trimmed = name.trim();
    if (!trimmed) {
      toastInfo("Nhập tên hồ sơ");
      return;
    }
    if (!current) {
      toastInfo("Chọn khổ giấy trước khi lưu hồ sơ");
      return;
    }
    savePrinterProfile({
      name: trimmed,
      paperSize: current.paperSize,
      orientation: current.orientation,
      customMm: current.customMm,
      note: note.trim() || undefined,
    });
    toastSuccess("Đã lưu hồ sơ máy in", trimmed);
    setName("");
    setNote("");
    setAdding(false);
    refresh();
  }

  function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    deletePrinterProfile(id);
    refresh();
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-foreground">Hồ sơ máy in</p>
        <button
          type="button"
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
          onClick={() => setAdding((v) => !v)}
        >
          <Plus className="h-3 w-3" /> Lưu khổ giấy hiện tại
        </button>
      </div>

      {adding && (
        <div className="space-y-1.5 rounded-md border bg-background p-2">
          <Input
            className="h-7 text-xs"
            placeholder="Tên hồ sơ (vd: Máy in A5 kho)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Input
            className="h-7 text-xs"
            placeholder="Ghi chú (tùy chọn — vị trí, loại máy…)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex items-center gap-1.5">
            <Button size="sm" className="h-7 flex-1 gap-1 text-xs" onClick={handleSaveCurrent}>
              <Check className="h-3 w-3" /> Lưu
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAdding(false)}>
              Hủy
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Đang lưu: {formatPaper(current)}
          </p>
        </div>
      )}

      {profiles.length > 0 ? (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-8 w-full items-center justify-between gap-1.5 rounded-md border bg-background px-2.5 text-xs"
            aria-expanded={open}
          >
            <span className="flex items-center gap-1.5 truncate">
              <Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-muted-foreground">
                Chọn hồ sơ ({profiles.length})
              </span>
            </span>
            <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
          </button>
          {open && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
              <ul className="max-h-56 overflow-auto py-0.5">
                {profiles.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left hover:bg-muted"
                      onClick={() => {
                        onApply(p);
                        setOpen(false);
                      }}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-medium">{p.name}</span>
                        <span className="block truncate text-[10px] text-muted-foreground">
                          {formatPaper({
                            paperSize: p.paperSize,
                            orientation: p.orientation,
                            customMm: p.customMm,
                          })}
                          {p.note ? ` · ${p.note}` : ""}
                        </span>
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-red-600"
                        title="Xóa hồ sơ"
                        onClick={(e) => handleDelete(p.id, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete(p.id, e as unknown as React.MouseEvent);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Chưa có hồ sơ. Chọn khổ giấy bên dưới rồi bấm "Lưu khổ giấy hiện tại".
        </p>
      )}
    </div>
  );
}
